import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './VerifyEmailPage.css';

const VerifyEmailPage = () => {
  const [code, setCode] = useState(new Array(6).fill(""));
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const inputsRef = useRef([]);
  const navigate = useNavigate();

  const handleChange = (index, value) => {
    if (!/^\d*$/.test(value)) return; // Allow only numbers

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    if (value && index < 5) {
      inputsRef.current[index + 1].focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData("text").slice(0, 6);
    if (!/^\d{1,6}$/.test(pasteData)) return;

    const newCode = pasteData.split("").concat(new Array(6 - pasteData.length).fill(""));
    setCode(newCode);

    // Focus on the last entered digit
    inputsRef.current[pasteData.length - 1]?.focus();
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace") {
      if (!code[index] && index > 0) {
        inputsRef.current[index - 1].focus();
      }
      const newCode = [...code];
      newCode[index] = "";
      setCode(newCode);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const currentEmail = localStorage.getItem('currentEmail');
    const newEmail = localStorage.getItem('newEmail');

    if (!currentEmail || !newEmail) {
      setError('Missing user information. Please enter your email again.');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch('https://api.hgcdental.clinic/reset-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ currentEmail, newEmail }),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.message || 'Failed to change email.');
      } else {
        localStorage.removeItem('currentEmail');
        localStorage.removeItem('newEmail');
        navigate('/profile'); // Redirect to profile or another page after update
      }
    } catch (error) {
      console.error('Error:', error);
      setError('An error occurred while updating the email.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setResendLoading(true);
    setError('');

    const email = localStorage.getItem('email');

    if (!email) {
      setError('Email not found. Please register again.');
      setResendLoading(false);
      return;
    }

    try {
      const response = await fetch('https://api.hgcdental.clinic/api/resend-otp', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.message || 'Failed to resend code.');
      } else {
        setError('A new code has been sent to your email.');
      }
    } catch (error) {
      console.error('Error:', error);
      setError('An error occurred while resending the code.');
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="verify-container">
      <div className="verify-box">
        <h2 className="verify-title">Verify Your Email</h2>
        <p className="verify-text">Enter the 6-digit code sent to your email</p>

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleVerify}>
          <div className="code-inputs">
            {code.map((digit, index) => (
              <input
                key={index}
                ref={(el) => (inputsRef.current[index] = el)}
                type="text"
                maxLength="1"
                value={digit}
                onChange={(e) => handleChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={handlePaste}
              />
            ))}
          </div>

          <button type="submit" className="verify-button" disabled={loading}>
            {loading ? 'Verifying...' : 'Verify Email'}
          </button>
        </form>

        <p className="resend-text">
          Didn't get code?{' '}
          <button className="resend-button" onClick={handleResendCode} disabled={resendLoading}>
            {resendLoading ? 'Resending...' : 'Resend'}
          </button>
        </p>
      </div>
    </div>
  );
};

export default VerifyEmailPage;